import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, User, Menu, ChevronDown } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../hooks/useLanguage';
import { Button } from '../ui/Button';
export function Navbar() {
  const {
    user,
    logout,
    isAuthenticated
  } = useAuth();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const handleLanguageChange = (lang: 'ar' | 'en') => {
    setLanguage(lang);
    setIsLangOpen(false);
  };
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <nav className="sticky top-0 z-50 w-full bg-white backdrop-blur-md border-b border-gray-200 shadow-sm" dir={language === 'ar' ? 'rtl' : 'ltr'} data-id="element-379">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-380">
        <div className="flex justify-between items-center h-16" data-id="element-381">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-id="element-382">
            <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 w-auto" data-id="element-383" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6" data-id="element-384">
            {/* Language Switcher - Dropdown Style */}
            <div className="relative" ref={langRef} data-id="element-385">
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-2 px-2 py-1.5 text-gray-600 hover:text-[#4f1964] transition-colors" data-id="element-386">
                <span className="text-lg" data-id="element-387">
                  {language === 'ar' ? '🇸🇦' : '🇺🇸'}
                </span>
                <span className="text-sm font-medium" data-id="element-388">
                  {language === 'ar' ? 'العربية' : 'English'}
                </span>
                <ChevronDown size={16} className={`transition-transform ${isLangOpen ? 'rotate-180' : ''}`} data-id="element-389" />
              </button>

              <AnimatePresence data-id="element-390">
                {isLangOpen && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.15
              }} className="absolute top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden min-w-[140px]" style={{
                [language === 'ar' ? 'right' : 'left']: 0
              }} data-id="element-391">
                    <button onClick={() => handleLanguageChange('ar')} className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${language === 'ar' ? 'text-[#4f1964] bg-purple-50' : 'text-gray-700'}`} data-id="element-392">
                      <span className="text-lg" data-id="element-393">🇸🇦</span>
                      <span data-id="element-394">العربية</span>
                    </button>
                    <button onClick={() => handleLanguageChange('en')} className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${language === 'en' ? 'text-[#4f1964] bg-purple-50' : 'text-gray-700'}`} data-id="element-395">
                      <span className="text-lg" data-id="element-396">🇺🇸</span>
                      <span data-id="element-397">English</span>
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {isAuthenticated ? <>
                <Link to="/dashboard" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-398">
                  {t('nav.dashboard')}
                </Link>
                <div className={`flex items-center gap-4 ${language === 'ar' ? 'mr-4 border-r pr-4' : 'ml-4 border-l pl-4'} border-gray-200`} data-id="element-399">
                  <div className="flex items-center gap-2" data-id="element-400">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-[#4f1964]" data-id="element-401">
                      <User size={16} data-id="element-402" />
                    </div>
                    <span className="text-sm font-medium text-gray-700" data-id="element-403">
                      {user?.name}
                    </span>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-100" data-id="element-404">
                    <LogOut size={16} className={language === 'ar' ? 'ml-2' : 'mr-2'} data-id="element-405" />
                    {t('nav.logout')}
                  </Button>
                </div>
              </> : <div className="flex items-center gap-3" data-id="element-406">
                <Link to="/login" data-id="element-407">
                  <Button variant="outline" size="sm" data-id="element-408">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register" data-id="element-409">
                  <Button variant="primary" size="sm" data-id="element-410">
                    {t('nav.getStarted')}
                  </Button>
                </Link>
              </div>}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2" data-id="element-411">
            {/* Mobile Language Switcher */}
            <div className="relative" ref={langRef} data-id="element-412">
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-1 px-2 py-1.5 text-gray-600" data-id="element-413">
                <span className="text-lg" data-id="element-414">
                  {language === 'ar' ? '🇸🇦' : '🇺🇸'}
                </span>
                <ChevronDown size={14} className={`transition-transform ${isLangOpen ? 'rotate-180' : ''}`} data-id="element-415" />
              </button>

              <AnimatePresence data-id="element-416">
                {isLangOpen && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.15
              }} className="absolute top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden min-w-[120px]" style={{
                [language === 'ar' ? 'right' : 'left']: 0
              }} data-id="element-417">
                    <button onClick={() => handleLanguageChange('ar')} className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${language === 'ar' ? 'text-[#4f1964] bg-purple-50' : 'text-gray-700'}`} data-id="element-418">
                      <span data-id="element-419">🇸🇦</span>
                      <span data-id="element-420">العربية</span>
                    </button>
                    <button onClick={() => handleLanguageChange('en')} className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${language === 'en' ? 'text-[#4f1964] bg-purple-50' : 'text-gray-700'}`} data-id="element-421">
                      <span data-id="element-422">🇺🇸</span>
                      <span data-id="element-423">English</span>
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" data-id="element-424">
              <Menu size={24} data-id="element-425" />
            </button>
          </div>
        </div>
      </div>
    </nav>;
}